<script setup lang="ts">
import { useModelFamilyPricings } from '@/composables/asicw/pricings'
import type { IMinerFamilyPricingFilters, IMinerModelFamilyPricing } from '@/types/asicw/pricings'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { h, reactive, ref } from 'vue'
import { useOIDCStore } from '@/stores/oidc.store'
import { tableColumnCurrency, tableColumnAdderCurrency } from '@/util/formatters.ts'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { Icon } from '@iconify/vue'
import { useDebounceFn } from '@vueuse/core'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'

const emit = defineEmits(['edit', 'visualize'])
const { isCustomer, isASICWManagerUser } = useOIDCStore()

const filters = reactive<IMinerFamilyPricingFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
})
const { customersModelFamilyPricingsQuery, modelFamilyPricingsQuery, deleteMinerFamilyPricingMutation } =
  useModelFamilyPricings()
const {
  data,
  isPending: isQueryPending,
  isError,
  error,
} = isCustomer ? customersModelFamilyPricingsQuery(filters) : modelFamilyPricingsQuery(filters)
const { mutateAsync: deleteAsync, isPending: isMutationPending } = deleteMinerFamilyPricingMutation

const onDelete = (pricing: IMinerModelFamilyPricing) =>
  DeleteConfirmationModal(
    'Delete Pricing',
    `Are you sure to delete pricing for model family ${pricing.miner_model_family}`,
    pricing.id,
    deleteAsync
  )

const GlassIcon = h(Icon, { icon: 'mdi:search' })

const fragmentFilter = ref('')
const debouncedSearch = useDebounceFn((query: string) => {
  filters.miner_model_family = query.trim() || undefined
}, 500)
</script>

<template>
  <RlErrorAlert v-if="isError" :error="error" />
  <el-input
    v-model="fragmentFilter"
    :prefix-icon="GlassIcon"
    class="w-96"
    placeholder="Model Family"
    clearable
    @input="debouncedSearch"
  />

  <!-- Customer View - Simple Table -->
  <el-table v-if="isCustomer" stripe :data="data?.data" :loading="isQueryPending" table-layout="fixed">
    <el-table-column label="Model Family" prop="miner_model_family" />
    <el-table-column label="M.F.A." prop="miner_model_family_abbreviation" />
    <el-table-column label="Price Starts From" prop="wholesale_base_cost" :formatter="tableColumnCurrency" />
  </el-table>

  <!-- Admin View - Full Table -->
  <el-table v-else stripe :data="data?.data" :loading="isQueryPending">
    <el-table-column label="Model Family" prop="miner_model_family" :width="230" fixed />
    <el-table-column label="SKU Details">
      <el-table-column label="M.F.A." prop="miner_model_family_abbreviation" :width="128" />
      <el-table-column label="W.C.B." prop="wholesale_base_cost" :width="128" :formatter="tableColumnCurrency" />

      <!-- Facility type -->
      <el-table-column
        class-name="text-blue-300"
        label="MDC Facility"
        prop="modular_facility_type_adder"
        :width="112"
        :formatter="tableColumnAdderCurrency"
      />
      <el-table-column
        class-name="text-blue-400"
        label="DC Facility"
        prop="traditional_facility_type_adder"
        :width="112"
        :formatter="tableColumnAdderCurrency"
      />

      <!-- Cooling method -->
      <el-table-column
        class-name="text-emerald-300"
        label="Air Cooling"
        prop="air_cooling_type_adder"
        :width="112"
        :formatter="tableColumnAdderCurrency"
      />
      <el-table-column
        class-name="text-emerald-400"
        label="Water Cooling"
        prop="water_cooling_type_adder"
        :width="112"
        :formatter="tableColumnAdderCurrency"
      />
      <el-table-column
        class-name="text-emerald-500"
        label="Immersion Cooling"
        prop="immersion_cooling_type_adder"
        :width="112"
        :formatter="tableColumnAdderCurrency"
      />

      <!-- Other details -->
      <el-table-column
        label="3PF"
        prop="third_party_firmware_adder"
        :width="128"
        :formatter="tableColumnAdderCurrency"
      />
      <el-table-column label="OC" prop="overclocking_adder" :width="128" :formatter="tableColumnAdderCurrency" />
    </el-table-column>
    <el-table-column label="Repair Costs">
      <!-- HB, colors hardcoded in design -->
      <el-table-column
        class-name="text-[#A7EBF7]"
        label="HB Basic Repair"
        prop="repair_costs.hashboard_basic_repair_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-[#44ABC9]"
        label="HB Advanced Repair"
        prop="repair_costs.hashboard_advanced_repair_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-[#6FDEFF]"
        label="HB Repl."
        prop="repair_costs.hashboard_replacement_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />

      <!-- PSU -->
      <el-table-column
        class-name="text-sprout-200"
        label="PSU Basic Repair"
        prop="repair_costs.power_supply_basic_repair_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-sprout-300"
        label="PSU Advanced Repair"
        prop="repair_costs.power_supply_advanced_repair_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-sprout-400"
        label="PSU Repl."
        prop="repair_costs.power_supply_replacement_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />

      <!-- CB -->
      <el-table-column
        class-name="text-xanthous-200"
        label="CB Basic Repair"
        prop="repair_costs.control_board_basic_repair_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-xanthous-300"
        label="CB Advanced Repair"
        prop="repair_costs.control_board_advanced_repair_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-xanthous-400"
        label="CB Repl."
        prop="repair_costs.control_board_replacement_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />

      <!-- Fan -->
      <el-table-column
        class-name="text-amethyst-300"
        label="Fan Basic Repair"
        prop="repair_costs.fan_basic_repair_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />
      <el-table-column
        class-name="text-amethyst-400"
        label="Fan Repl."
        prop="repair_costs.fan_replacement_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />
    </el-table-column>
    <el-table-column label="Labor Costs">
      <!-- HB, colors hardcoded in design -->
      <el-table-column
        class-name="text-[#A7EBF7]"
        label="HB"
        prop="additional_costs.hashboard_labor_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <!-- PSU -->
      <el-table-column
        class-name="text-sprout-200"
        label="PSU"
        prop="additional_costs.power_supply_labor_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <!-- CB -->
      <el-table-column
        class-name="text-xanthous-200"
        label="CB"
        prop="additional_costs.control_board_labor_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <!-- Fan -->
      <el-table-column
        class-name="text-amethyst-200"
        label="Fan"
        prop="additional_costs.fan_labor_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />
    </el-table-column>
    <el-table-column label="Shipping Costs">
      <!-- HB, colors hardcoded in design -->
      <el-table-column
        class-name="text-[#44ABC9]"
        label="HB"
        prop="additional_costs.hashboard_shipping_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <!-- PSU -->
      <el-table-column
        class-name="text-sprout-300"
        label="PSU"
        prop="additional_costs.power_supply_shipping_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />
      <!-- CB -->
      <el-table-column
        class-name="text-xanthous-300"
        label="CB"
        prop="additional_costs.control_board_shipping_cost"
        :width="112"
        :formatter="tableColumnCurrency"
      />

      <!-- Fan -->
      <el-table-column
        class-name="text-amethyst-300"
        label="Fan"
        prop="additional_costs.fan_shipping_cost"
        :width="128"
        :formatter="tableColumnCurrency"
      />
    </el-table-column>
    <el-table-column v-if="isASICWManagerUser" :width="128" fixed="right">
      <template #default="{ row }">
        <div class="flex flex-row">
          <el-button link title="Overview" @click="emit('visualize', row)"
            ><Icon inline icon="material-symbols:overview-outline" :height="16"
          /></el-button>
          <el-button link title="Edit" @click="emit('edit', row)"
            ><Icon inline icon="material-symbols:edit-outline-rounded" :height="16"
          /></el-button>
          <el-button
            link
            title="Delete"
            :loading="isMutationPending"
            :disabled="isMutationPending"
            @click="onDelete(row as IMinerModelFamilyPricing)"
            ><Icon inline icon="mdi:trash-can-outline" :height="16"
          /></el-button>
        </div>
      </template>
    </el-table-column>
  </el-table>
  <el-pagination
    v-if="data && data.total_pages > 0"
    v-model:current-page="filters.page"
    v-model:page-size="filters.per_page"
    class="self-end"
    :page-sizes="DEFAULT_PAGE_SIZES"
    :page-count="data.total_pages"
    :total="data.total_records"
    :disabled="isQueryPending"
    :layout="DEFAULT_PAGINATION_LAYOUT"
    background
  />
</template>

<style scoped lang="scss">
.el-table--border {
  &::before {
    top: 0;
    width: 0;
    height: 0;
  }

  &::after {
    top: 0;
    width: 0;
    height: 0;
  }
}

:deep() {
  .el-table {
    &__header {
      tr {
        th {
          background-color: var(--fill-primary) !important;
          color: var(--text-tertiary) !important;
        }
      }
    }

    &__cell {
      border-right: none;
    }

    &__inner-wrapper {
      &::after {
        width: 0;
        height: 0;
      }
    }

    &__border-left-patch {
      width: 0;
      height: 0;
    }

    &__row:last-child .el-table__cell {
      border-bottom: 0;
    }

    .last-real-column {
      border-right: 0;
    }
  }
}
</style>
